import Head from 'next/head';
import * as React from 'react';
import Image from 'next/image';
import { useRef } from 'react';
import { Button, Section } from 'react-scale/components';
import { SummitHeader } from 'react-scale/components/pages/summit/header';
import { Drawer } from 'react-scale/components/pages/summit/drawer';
import Spline from '@splinetool/react-spline';
import heroText from 'react-scale/public/static/summit/hero-text.svg';
import isologo from 'react-scale/public/static/summit/isologo.png';
import { useDeviceDetect } from 'react-scale/hooks/use-device-detect';
import logo from 'react-scale/public/static/summit/logo.svg';

export const SummitHero = ({ dates, title, paragraphs = [] }) => {
  const { isMobile } = useDeviceDetect();
  const isologoRef = useRef<HTMLImageElement>();

  return (
    <>
      <Head>
        <style dangerouslySetInnerHTML={{ __html: `body {overflow-x: hidden;}` }} />
      </Head>
      <SummitHeader />
      <Section
        paddingTop={false}
        paddingBottom={false}
        className="relative min-h-screen lg:min-h-none gap-y-8 gap-x-[60px] text-center lg:text-left grid lg:gap-y-6 grid-rows-[1fr_repeat(5,_auto)] lg:grid-rows-[repeat(3,_auto)_1fr_auto] lg:grid-cols-2 items-center pb-20 pt-[90px] lg:pt-[20vh] lg:pb-[19vh]"
      >
        <h1 className="sr-only">Scale Summit</h1>
        <figure className="min-h-[368px] relative -top-1 w-full max-h-[500px] md:min-h-[440px] lg:min-h-[318px] h-full object-contain lg:row-span-full mb-5 lg:mb-0">
          {isMobile === false && (
            <Spline
              onLoad={spline => {
                setTimeout(() => {
                  if (!isologoRef.current) return;

                  isologoRef.current.style.opacity = '0';
                  spline.canvas.style.opacity = '1';
                }, 200);
              }}
              scene="https://prod.spline.design/N2XfoSUln-l-SVEf/scene.splinecode"
              className="min-w-[135%] md:min-h-[135%] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 opacity-0 transition-opacity duration-100 delay-100"
            />
          )}
          <Image
            priority
            src={heroText}
            alt="Scale Summit"
            className="absolute top-5 w-[70%] h-auto max-h-[280px] lg:max-h-none -translate-x-1/2 left-1/2 pointer-events-none"
          />
          <Image
            ref={isologoRef}
            src={isologo}
            alt="Summit logo"
            className="absolute bottom-0 w-full transition-opacity duration-100"
            priority
            quality={100}
            sizes="100vw, (min-width: 1024px) 50vw"
          />
          <Image
            priority
            src={logo}
            alt="by Scale"
            className="z-50 absolute -bottom-5 md:-bottom-1 lg:bottom-3 min-w-[120px] w-[20%] h-auto -translate-x-1/2 left-1/2 pointer-events-none"
          />
          <div className="absolute bottom-0 w-full h-2/5 bg-gradient-to-t from-black to-transparent" />
        </figure>
        <span className="uppercase lg:font-medium text-gradient-white text-xl tracking-[3px]">
          {dates}
        </span>
        <h2 className="font-forest font-[250] xl:pr-18 mb-5 lg:mb-0 text-[45px] lg:text-6xl xl:text-6.5xl leading-[1.1]">
          {title}
        </h2>
        <div className="mb-3 space-y-5 lg:mb-0">
          {paragraphs.map(({ paragraph }, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <p key={index}>{paragraph}</p>
          ))}
        </div>
        <Drawer>
          {/* @ts-expect-error */}
          <Button
            text="Pre-Register"
            typeButton={{ className: 'mt-6 !mx-0 lg:w-fit' }}
            type="primary"
            variant="grayscale"
          />
        </Drawer>
      </Section>
    </>
  );
};
